import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";
import ShippingMethodApi from "../Api/ShippingMethodApi";
import usePermissions from "../hook/usePermissions";
import ShowNotifications from "../helper/ShowNotifications";

const ShippingMethodLayer = () => {
    const { hasPermission } = usePermissions();
    const [type, setType] = useState("amount"); // weight, pincode, amount
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [errors, setErrors] = useState({});

    const typeOptions = [
        { value: "weight", label: "Total Product Weight Based" },
        { value: "pincode", label: "Address Pincode Based" },
        { value: "amount", label: "Order Amount Based" },
    ];

    useEffect(() => {
        fetchActiveMethod();
    }, []);

    const fetchActiveMethod = async () => {
        setFetching(true);
        const result = await ShippingMethodApi.getActive();
        if (result.status && result.response.data) {
            const data = result.response.data;
            // Always use amount based as per request
            setType("amount");
            const rawRules = data.type === "amount" ? (data.rules || []) : [];
            const sanitizedRules = rawRules.map(rule => ({
                ...rule,
                tnDiscount: rule.tnDiscount === undefined || rule.tnDiscount === null ? 0 : rule.tnDiscount,
                osDiscount: rule.osDiscount === undefined || rule.osDiscount === null ? 0 : rule.osDiscount,
            }));
            setRules(sanitizedRules);
        }
        setFetching(false);
    };

    const handleTypeChange = (selectedOption) => {
        setType(selectedOption.value);
        // Reset rules when type changes, or if we have them saved in an object for each type we could restore
        // For simplicity, let's just clear or keep if user wants to switch back
        setRules([]);
    };

    const addRule = () => {
        if (type === "weight") {
            setRules([...rules, { weight: "", amount: "" }]);
        } else if (type === "pincode") {
            setRules([...rules, { pincode: "", amount: "" }]);
        } else if (type === "amount") {
            setRules([...rules, { from: "", to: "", tnAmount: "", tnDiscount: 0, osAmount: "", osDiscount: 0 }]);
        }
    };

    const removeRule = (index) => {
        const updatedRules = rules.filter((_, i) => i !== index);
        setRules(updatedRules);
    };

    const handleRuleChange = (index, field, value) => {
        // Only allow non-negative values for numerical fields
        if (field !== "pincode" && value !== "" && parseFloat(value) < 0) {
            return;
        }
        const updatedRules = [...rules];
        updatedRules[index][field] = value;
        setRules(updatedRules);

        // Clear error when user types
        if (errors[`${index}-${field}`]) {
            setErrors(prev => {
                const next = { ...prev };
                delete next[`${index}-${field}`];
                return next;
            });
        }
    };

    const handleKeyDown = (e) => {
        // Block signs like + and - as well as 'e' for scientific notation
        if (["e", "E", "+", "-"].includes(e.key)) {
            e.preventDefault();
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const newErrors = {};
        rules.forEach((rule, index) => {
            if (type === "amount") {
                if (rule.from === "") newErrors[`${index}-from`] = "Required";
                if (rule.to === "") newErrors[`${index}-to`] = "Required";
                if (rule.tnAmount === "") newErrors[`${index}-tnAmount`] = "Required";
                if (rule.osAmount === "") newErrors[`${index}-osAmount`] = "Required";
            } else if (type === "weight") {
                if (rule.weight === "") newErrors[`${index}-weight`] = "Required";
                if (rule.amount === "") newErrors[`${index}-amount`] = "Required";
            } else if (type === "pincode") {
                if (rule.pincode === "") newErrors[`${index}-pincode`] = "Required";
                if (rule.amount === "") newErrors[`${index}-amount`] = "Required";
            }
        });

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            ShowNotifications.showAlertNotification("Please fill all required fields", false);
            return;
        }

        if (rules.length === 0) {
            ShowNotifications.showAlertNotification("Please add at least one rule", false);
            return;
        }

        setLoading(true);

        const processedRules = rules.map(rule => {
            const newRule = { ...rule };
            if (newRule.weight !== undefined) newRule.weight = Number(newRule.weight);
            if (newRule.amount !== undefined) newRule.amount = Number(newRule.amount);
            if (newRule.from !== undefined) newRule.from = Number(newRule.from);
            if (newRule.to !== undefined) newRule.to = Number(newRule.to);
            if (newRule.tnAmount !== undefined) newRule.tnAmount = Number(newRule.tnAmount);
            newRule.tnDiscount = rule.tnDiscount === "" || rule.tnDiscount === null || rule.tnDiscount === undefined ? 0 : Number(rule.tnDiscount);
            if (newRule.osAmount !== undefined) newRule.osAmount = Number(newRule.osAmount);
            newRule.osDiscount = rule.osDiscount === "" || rule.osDiscount === null || rule.osDiscount === undefined ? 0 : Number(rule.osDiscount);
            if (newRule.pincode !== undefined) {
                // Keep pincode as string if it can have leading zeros or contains letters (if applicable), 
                // but the user's sample only mentioned from, to, and amount for the amount based type.
                // Generally, pincodes are stored as strings to preserve leading zeros.
            }
            return newRule;
        });

        const payload = {
            type,
            rules: processedRules,
            isActive: true
        };

        await ShippingMethodApi.save(payload);
        setLoading(false);
    };

    if (fetching) {
        return (
            <div className="d-flex justify-content-center align-items-center h-100">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24">
                <h6 className="text-primary-600 pb-2 mb-0">Shipping Methods Configuration</h6>
            </div>
            <div className="card-body p-24">
                <form onSubmit={handleSubmit} noValidate>
                    <div className="row gy-4">
                        <div className="col-md-12">
                            <div className="d-flex justify-content-between align-items-center mb-16">
                                <h6 className="mb-0 text-md fw-bold text-primary-600">Rules for Order Amount Based Shipping</h6>
                                {(hasPermission("Shipping Methods", "add") || hasPermission("Shipping Methods", "edit")) && (
                                    <button
                                        type="button"
                                        className="btn btn-primary-600 radius-8 px-20 py-8 d-flex align-items-center gap-2"
                                        onClick={addRule}
                                        style={{ backgroundColor: "#364e3d", borderColor: "#364e3d" }}
                                    >
                                        <Icon icon="lucide:plus" width="18" height="18" />
                                        Add {type === "pincode" ? "Pincode" : type === "weight" ? "Weight" : "Amount Range"}
                                    </button>
                                )}
                            </div>

                            <div className="table-responsive">
                                <table className="table border-top">
                                    <thead>
                                        <tr>
                                            <th className="bg-light-600 text-center" style={{ width: '50px' }}>S.No</th>
                                            {type === "weight" && (
                                                <>
                                                    <th className="bg-light-600">Max Weight (kg) <span className="text-danger">*</span></th>
                                                    <th className="bg-light-600">Shipping Charge <span className="text-danger">*</span></th>
                                                </>
                                            )}
                                            {type === "pincode" && (
                                                <>
                                                    <th className="bg-light-600">Pincode <span className="text-danger">*</span></th>
                                                    <th className="bg-light-600">Shipping Charge <span className="text-danger">*</span></th>
                                                </>
                                            )}
                                            {type === "amount" && (
                                                <>
                                                    <th className="bg-light-600 px-8" style={{ width: '160px' }}>From Amount <span className="text-danger">*</span></th>
                                                    <th className="bg-light-600 px-8" style={{ width: '160px' }}>To Amount <span className="text-danger">*</span></th>
                                                    <th className="bg-light-600 text-center px-4" style={{ width: '125px', fontSize: '12px' }}>TamilNadu <br /> Charges <span className="text-danger">*</span></th>
                                                    <th className="bg-light-600 text-center px-4" style={{ width: '125px', fontSize: '12px' }}>TamilNadu <br /> Discount </th>
                                                    <th className="bg-light-600 text-center px-4" style={{ width: '125px', fontSize: '12px' }}>Other States <br /> Charges <span className="text-danger">*</span></th>
                                                    <th className="bg-light-600 text-center px-4" style={{ width: '125px', fontSize: '12px' }}>Other States <br /> Discount </th>
                                                </>
                                            )}
                                            <th className="bg-light-600 text-center" style={{ width: '80px' }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rules.length === 0 ? (
                                            <tr>
                                                <td colSpan={type === "amount" ? 8 : 4} className="text-center py-4">
                                                    No rules added yet. Click "Add" to get started.
                                                </td>
                                            </tr>
                                        ) : (
                                            rules.map((rule, index) => (
                                                <tr key={index}>
                                                    <td className="text-center fw-bold">{index + 1}</td>
                                                    {type === "weight" && (
                                                        <>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    className="form-control radius-8"
                                                                    placeholder="e.g. 10"
                                                                    value={rule.weight}
                                                                    onChange={(e) => handleRuleChange(index, "weight", e.target.value)}
                                                                    min="0"
                                                                    required
                                                                    disabled={!hasPermission("Shipping Methods", "edit")}
                                                                />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    className="form-control radius-8"
                                                                    placeholder="e.g. 50"
                                                                    value={rule.amount}
                                                                    onChange={(e) => handleRuleChange(index, "amount", e.target.value)}
                                                                    min="0"
                                                                    required
                                                                    disabled={!hasPermission("Shipping Methods", "edit")}
                                                                />
                                                            </td>
                                                        </>
                                                    )}
                                                    {type === "pincode" && (
                                                        <>
                                                            <td>
                                                                <input
                                                                    type="text"
                                                                    className="form-control radius-8"
                                                                    placeholder="e.g. 600001"
                                                                    value={rule.pincode}
                                                                    onChange={(e) => handleRuleChange(index, "pincode", e.target.value)}
                                                                    required
                                                                    disabled={!hasPermission("Shipping Methods", "edit")}
                                                                />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    className="form-control radius-8"
                                                                    placeholder="e.g. 50"
                                                                    value={rule.amount}
                                                                    onChange={(e) => handleRuleChange(index, "amount", e.target.value)}
                                                                    min="0"
                                                                    required
                                                                    disabled={!hasPermission("Shipping Methods", "edit")}
                                                                />
                                                            </td>
                                                        </>
                                                    )}
                                                    {type === "amount" && (
                                                        <>
                                                            <td className="px-8">
                                                                <input
                                                                    type="number"
                                                                    className="form-control radius-8"
                                                                    placeholder="From Amount"
                                                                    style={{ minWidth: '130px' }}
                                                                    value={rule.from}
                                                                    onChange={(e) => handleRuleChange(index, "from", e.target.value)}
                                                                    onKeyDown={handleKeyDown}
                                                                    min="0"
                                                                    required
                                                                    disabled={!hasPermission("Shipping Methods", "edit")}
                                                                />
                                                                {errors[`${index}-from`] && <small className="text-danger">{errors[`${index}-from`]}</small>}
                                                            </td>
                                                            <td className="px-8">
                                                                <input
                                                                    type="number"
                                                                    className="form-control radius-8"
                                                                    placeholder="To Amount"
                                                                    style={{ minWidth: '130px' }}
                                                                    value={rule.to}
                                                                    onChange={(e) => handleRuleChange(index, "to", e.target.value)}
                                                                    onKeyDown={handleKeyDown}
                                                                    min="0"
                                                                    required
                                                                    disabled={!hasPermission("Shipping Methods", "edit")}
                                                                />
                                                                {errors[`${index}-to`] && <small className="text-danger">{errors[`${index}-to`]}</small>}
                                                            </td>
                                                            <td className="px-4">
                                                                <input
                                                                    type="number"
                                                                    className="form-control radius-8 text-center"
                                                                    placeholder="Charges"
                                                                    style={{ minWidth: '110px' }}
                                                                    value={rule.tnAmount}
                                                                    onChange={(e) => handleRuleChange(index, "tnAmount", e.target.value)}
                                                                    onKeyDown={handleKeyDown}
                                                                    min="0"
                                                                    required
                                                                    disabled={!hasPermission("Shipping Methods", "edit")}
                                                                />
                                                                {errors[`${index}-tnAmount`] && <small className="text-danger">{errors[`${index}-tnAmount`]}</small>}
                                                            </td>
                                                            <td className="px-4">
                                                                <input
                                                                    type="number"
                                                                    className="form-control radius-8 text-center"
                                                                    placeholder="Discount"
                                                                    style={{ minWidth: '110px' }}
                                                                    value={rule.tnDiscount}
                                                                    onChange={(e) => handleRuleChange(index, "tnDiscount", e.target.value)}
                                                                    onKeyDown={handleKeyDown}
                                                                    min="0"
                                                                    disabled={!hasPermission("Shipping Methods", "edit")}
                                                                />
                                                            </td>
                                                            <td className="px-4">
                                                                <input
                                                                    type="number"
                                                                    className="form-control radius-8 text-center"
                                                                    placeholder="Charges"
                                                                    style={{ minWidth: '110px' }}
                                                                    value={rule.osAmount}
                                                                    onChange={(e) => handleRuleChange(index, "osAmount", e.target.value)}
                                                                    onKeyDown={handleKeyDown}
                                                                    min="0"
                                                                    required
                                                                    disabled={!hasPermission("Shipping Methods", "edit")}
                                                                />
                                                                {errors[`${index}-osAmount`] && <small className="text-danger">{errors[`${index}-osAmount`]}</small>}
                                                            </td>
                                                            <td className="px-4">
                                                                <input
                                                                    type="number"
                                                                    className="form-control radius-8 text-center"
                                                                    placeholder="Discount"
                                                                    style={{ minWidth: '110px' }}
                                                                    value={rule.osDiscount}
                                                                    onChange={(e) => handleRuleChange(index, "osDiscount", e.target.value)}
                                                                    onKeyDown={handleKeyDown}
                                                                    min="0"
                                                                    disabled={!hasPermission("Shipping Methods", "edit")}
                                                                />
                                                            </td>
                                                        </>
                                                    )}
                                                    <td className="text-center">
                                                        {hasPermission("Shipping Methods", "delete") && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-icon btn-danger-100 text-danger-600 rounded-circle"
                                                                onClick={() => removeRule(index)}
                                                            >
                                                                <Icon icon="mingcute:delete-2-line" width="20" height="20" />
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {(hasPermission("Shipping Methods", "add") || hasPermission("Shipping Methods", "edit")) && (
                        <div className="d-flex justify-content-end gap-2 mt-24">
                            <button
                                type="submit"
                                className="btn btn-primary radius-8 px-32 py-12 justify-content-center d-flex align-items-center gap-2"
                                disabled={loading}
                                style={{ backgroundColor: "#364e3d", borderColor: "#364e3d" }}
                            >
                                {loading && (
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                )}
                                {loading ? "Saving..." : "Save Settings"}
                            </button>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default ShippingMethodLayer;

